import type {Tx} from "../mate/mate-types";
import type {AccountId} from "../../cdx-models/Account";
import type {CardId} from "../../cdx-models/Card";
import type {UserId} from "../../cdx-models/User";
import {implOptimistic} from "./types";

/** Starting and stopping a card's timer only happens on its own in strict time tracking. */
const getStrictContext = (tx: Tx) => {
  const accountId = tx.get<AccountId>("_root", "_root", "account");
  if (tx.get("account", accountId, "timeTrackingMode") !== "strict") return null;
  return {accountId, userId: tx.get<UserId>("_root", "_root", "loggedInUser")};
};

const finishSegments = (tx: Tx, isAffected: (segmentId: string) => boolean) => {
  const finishedAt = new Date();
  for (const {id} of tx.all("timeTrackingSegment")) {
    if (tx.get("timeTrackingSegment", id, "finishedAt", undefined) !== null) continue;
    if (isAffected(id)) tx.update("timeTrackingSegment", id, {finishedAt});
  }
};

type StartedTracker = {
  activeTracker?: {id: string; cardId: CardId};
  startedSegment?: {id: string; cardId: CardId};
};

export const startTrackerOptimistically = (cardId: CardId) =>
  implOptimistic(
    (tx) => {
      const strict = getStrictContext(tx);
      if (!strict) return;
      const {accountId, userId} = strict;
      const now = new Date();
      finishSegments(tx, (id) => tx.get("timeTrackingSegment", id, "user", null) === userId);

      const trackerId = tx.get<string | null>("user", userId, "activeTimeTracker", null);
      if (trackerId) {
        tx.update("activeTimeTracker", trackerId, {card: cardId});
      } else {
        const id = tx.create(
          "activeTimeTracker",
          {card: cardId, user: userId, account: accountId, createdAt: now},
          {key: cardId}
        );
        // the relation's sql names the current organization, which the cache can't check
        tx.update("user", userId, {activeTimeTracker: id});
      }
      tx.create(
        "timeTrackingSegment",
        {
          card: cardId,
          user: userId,
          account: accountId,
          createdAt: now,
          startedAt: now,
          modifyDurationMsBy: 0,
          finishedAt: null,
        },
        {key: cardId}
      );
    },
    {
      createdIds: ({activeTracker, startedSegment}: StartedTracker = {}) => ({
        ...(activeTracker && {activeTimeTracker: {[activeTracker.cardId]: activeTracker.id}}),
        ...(startedSegment && {timeTrackingSegment: {[startedSegment.cardId]: startedSegment.id}}),
      }),
    }
  );

export const stopTrackerOptimistically = (cardId: CardId) =>
  implOptimistic((tx) => {
    if (!getStrictContext(tx)) return;
    finishSegments(tx, (id) => tx.get("timeTrackingSegment", id, "card", null) === cardId);
  });
